import { Tooltip, Typography } from "antd";
import { chunk, get } from "lodash";
import React, { useState } from "react";
import { isMobile } from "react-device-detect";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import NafutelRootLoadingComponent from "src/components/nafutel-loading";
import NafutelPaginationComponent from "src/components/nafutel-pagination";
import NafutelTableComponent from "src/components/nafutel-table";
import {
  IColumn,
  INafutelPagination,
  IResaleHotelModel,
  IStatusCode,
  PATH_URL,
} from "src/interfaces";
import { sellOptions } from "src/pages/reservation-list/components/search/types";
import { UnAuthServices } from "src/services";
import { convertUtcToJapanDate } from "src/utils/datetime";
import { formatPrice } from "src/utils/format-price";
import { PRODUCT_TYPE_SELLING, PRODUCT_TYPE_SOLD } from "../../types";
import SearchComponent from "../search";

const { Paragraph } = Typography;

interface IProps {
  isResale?: boolean;
}

function ReservationListTab2Component({ isResale }: IProps) {
  const { t } = useTranslation();

  const [list, setList] = useState<IResaleHotelModel[] | null>([]);
  const [initList, setInitList] = useState<IResaleHotelModel[][]>([]);
  const [pagination, setPagination] = useState<INafutelPagination>({
    total: 0,
    size: 10,
    page: 1,
  });

  const [loading, setLoading] = useState(false);
  const navigrate = useNavigate();
  const columns: IColumn[] = [
    {
      title: t("reservation_list.table_id"),
      dataIndex: "sort",
      alignCenter: true,
      style: { backgroundColor: "#182D4A", color: "#ffffff" },
      tdCss: {
        backgroundColor: "#182D4A",
        color: "#ffffff",
        fontSize: 14,
      },
      render: (row) => {
        return (
          <Tooltip
            title={() => {
              return <div>{row?.sort}</div>;
            }}
          >
            <span>{row?.sort?.substring(row?.sort?.length - 5)}</span>
          </Tooltip>
        );
      },
    },
    {
      title: t("reservation_list.table_type"),
      dataIndex: "reservation_type",
      alignCenter: true,
      style: { backgroundColor: "#9C8B58", color: "#ffffff" },
      tdCss: (row) => {
        const status = get(row, "p_status");
        return {
          backgroundColor: status === 0 ? "#3F9BC0" : "#9C8B58",
          color: "#ffffff",
          width: "80px",
          fontSize: 14,
        };
      },
      render: (row) => {
        const status = Number(get(row, "p_status"));
        switch (status) {
          case PRODUCT_TYPE_SELLING:
            return t("reservationList.search.product_status.new");
          case PRODUCT_TYPE_SOLD:
            return t("reservationList.search.product_status.sold");
          default:
            return status;
        }
      },
    },
    {
      title: t("reservation_list.on_sale_table.title"),
      dataIndex: "title",
      tdCss: {
        width: "160px",
        color: " #000000",
        fontSize: 14,
        fontWeight: 400,
      },
      alignCenter: true,
    },
    {
      title: (
        <div
          dangerouslySetInnerHTML={{
            __html: `${t(
              "reservation_list.on_sale_table.hotel_room_plan_name"
            )}`,
          }}
        />
      ),

      tdCss: {
        width: "160px",
        color: " #000000",
        fontSize: 14,
        fontWeight: 400,
      },
      alignCenter: true,
      render: (row) => {
        return (
          <div>
            <div style={{ maxWidth: 150 }}>
              <Tooltip title={row.hotel.name}>
                <Paragraph
                  className="m-0"
                  ellipsis={true}
                  style={{ color: "#000" }}
                >
                  {row.hotel.name}
                </Paragraph>
              </Tooltip>
            </div>
            <div style={{ maxWidth: 150 }}>
              <Tooltip title={row.room.room_name}>
                <Paragraph
                  className="m-0"
                  ellipsis={true}
                  style={{ color: "#000" }}
                >
                  {row.room.room_name}
                </Paragraph>
              </Tooltip>
            </div>
            <div style={{ maxWidth: 150 }}>
              <Tooltip title={row.plan.name}>
                <Paragraph
                  className="m-0"
                  ellipsis={true}
                  style={{ color: "#000" }}
                >
                  {row.plan.name}
                </Paragraph>
              </Tooltip>
            </div>
          </div>
        );
      },
    },
    {
      title: t("reservation_list.table.total_price"),
      tdCss: { width: "100px" },
      alignCenter: true,
      render: (row) => {
        return (
          <div>
            <span style={{ fontWeight: 600 }}>
              {formatPrice(get(row, "reservation.total_price", 0))}
            </span>
            {t("common.money.unit")}
          </div>
        );
      },
    },
    {
      title: t("reservation_list.table.price"),
      tdCss: { width: "100px" },
      alignCenter: true,
      render: (row) => {
        return (
          <div>
            <span style={{ fontWeight: 600 }}>
              {formatPrice(get(row, "price", 0))}
            </span>
            {t("common.money.unit")}
          </div>
        );
      },
    },
    {
      title: t("reservation_list.table_date"),
      tdCss: { width: "154px", fontSize: 14, color: " #000000" },
      alignCenter: true,
      render: (row) => {
        return `${convertUtcToJapanDate(
          get(row, "start_date")
        )}~${convertUtcToJapanDate(get(row, "end_date"))}`;
      },
    },
    {
      title: t("reservation_list.table.guest"),
      dataIndex: "guests",
      tdCss: {
        width: "130px",
        color: " #000000",
        fontSize: 14,
        fontWeight: 400,
      },
      alignCenter: true,
      render: (row) => {
        return convertUtcToJapanDate(get(row, "public_end_date"));
      },
    },
  ];

  const handleApi = (condition: any) => {
    setLoading(true);
    UnAuthServices.getListResaleBySaller(condition)
      .then((response) => {
        if (
          response &&
          response.data &&
          response.data.resale_reservations &&
          response.status_code === IStatusCode.OK
        ) {
          const data = (response.data.resale_reservations || []).filter(
            (ele: IResaleHotelModel) => ele.status !== "expired"
          );
          const list_chunk: any[] = chunk(data, pagination.size);
          setList(list_chunk[0] || []);
          setInitList(list_chunk);
          setPagination((pre) => {
            return {
              ...pre,
              total: data.length || 0,
            };
          });
        }
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const onChangePagination = (page: any) => {
    setPagination((pre: any) => {
      return {
        ...pre,
        page,
      };
    });
    const index = page - 1;
    if (initList[index]) {
      setList(initList[index]);
    }
  };

  return (
    <React.Fragment>
      {loading && <NafutelRootLoadingComponent />}
      <div style={{ marginTop: isMobile ? 30 : 20 }}>
        <SearchComponent
          parentCss={{
            position: "relative",
            gridTemplateColumns: !isMobile ? "auto 160px" : "",
            backgroundColor: isMobile ? "transparent" : "#24305A",
          }}
          styleLeftBg={{
            backgroundColor: "#ffffff",
            border: "none",
            boxShadow: "0px 0px 4px rgba(0, 0, 0, 0.16)",
            padding: 15,
          }}
          styleRightBg={{
            backgroundColor: "#24305A",
            color: "#ffffff",
          }}
          styleTextCheckbox={{ color: "#000000" }}
          listCheckbox={sellOptions()}
          callback={handleApi}
        />
      </div>
      <div>
        <NafutelTableComponent
          columns={columns}
          dataSource={list}
          styleHeader={{ height: 48, fontSize: 14 }}
          styleTable={{ marginTop: 32 }}
          rowStyle={{ textAlign: "center", fontSize: 14 }}
          rowClick={(row) => {
            const url =
              row.p_status === PRODUCT_TYPE_SOLD
                ? PATH_URL.RESALE_RESERVATION_DETAIL +
                  "/" +
                  get(row, "sort") +
                  "?menu=2&reservation_list_tab_active=1"
                : `${PATH_URL.RESALE_DETAIL}/${get(row, "sort")}`;
            return navigrate(url);
          }}
        />
        <NafutelPaginationComponent
          pagination={pagination}
          onChange={onChangePagination}
          style={{ marginTop: 20, display: "flex", justifyContent: "center" }}
        />
      </div>
    </React.Fragment>
  );
}

export default ReservationListTab2Component;
